import { useCallback, useMemo } from 'react';

import { getApplicableL2CompanyModules } from 'Deer/components/navigation/secondary/Company/CompanyModules';
import { getApplicableL2MyDataModules } from 'Deer/components/navigation/secondary/ModuleSection';
import {
  getApplicableL2MyTeamModules,
  getApplicableL2ViewModules,
  getViewRoute,
} from 'Deer/components/navigation/secondary/MyTeam/MyTeamModules';
import { routes } from 'Deer/constants';
import { staticRoutes } from 'Deer/constants/routes';
import { useSelectCompetenciesSelectedIntent } from 'Deer/intent/Competencies/slice/CompetenciesIntentSlice';
import { useSelectDisciplinaryActionsSelectedIntent } from 'Deer/intent/DisciplinaryActions/slice/DisciplinaryActionsIntentSlice';
import { useSelectEngagementSelectedIntent } from 'Deer/intent/Engagement/slice/EngagementIntentSlice';
import { useSelectGoalsSelectedIntent } from 'Deer/intent/Goals/slice/GoalsIntentSlice';
import { useSelectReviewsSelectedIntent } from 'Deer/intent/Reviews/slice/ReviewsIntentSlice';
import { DEFAULT_INTENTS_FOR_MODULES } from 'Deer/intent/constants';
import { useSelectInitiativeSelectedIntent } from 'Deer/intent/initiative/slice/InitiativeIntentSlice';
import { EnabledFeatures } from 'Deer/types/logeedInUsersAccesses';
import useOrganisationSettings from 'components/hooks/useOrganisationSettings';
import { useRouteMatch } from 'react-router-dom';
import { useOneOnOneInsightsFiltersData } from 'screens/oneOnOneRabbit/Insights/slice/OneOnOneInsightsFiltersSlice';

import { useRouteToNavLevels } from './useRouteToL3';

type L1Module = 'myData' | 'team' | 'company';
type TeamType = 'directReports' | 'allReports' | 'view';

export const useL1IntentNavigation = () => {
  const { value: selectedGoalsIntentValue } = useSelectGoalsSelectedIntent() ?? {};
  const { value: selectedInitiativesIntentValue } = useSelectInitiativeSelectedIntent() ?? {};
  const { selectedIntent: selectedOneOnOneIntent } = useOneOnOneInsightsFiltersData() ?? {};
  const { value: selectedReviewsIntentValue } = useSelectReviewsSelectedIntent() ?? {};
  const { selectedIntent: selectedCompetenciesIntent } = useSelectCompetenciesSelectedIntent() ?? {};
  const selectedEngagementIntent = useSelectEngagementSelectedIntent();
  const selectedDisciplinaryIntent = useSelectDisciplinaryActionsSelectedIntent();
  const { moduleName } = useOrganisationSettings();
  const { l2Module } = useRouteToNavLevels();

  const { isExact: isHome } = useRouteMatch(routes.home);

  const applicableL2MyDataModules = getApplicableL2MyDataModules(moduleName) ?? [];
  const applicableL2DirectReportsModules = getApplicableL2MyTeamModules('directReports', moduleName);
  const applicableL2AllReportsModules = getApplicableL2MyTeamModules('allReports', moduleName);
  const applicableL2CompanyModules = getApplicableL2CompanyModules(moduleName);

  const SELECTED_INTENT_VALUES = useMemo(
    () => ({
      goal: selectedGoalsIntentValue,
      initiative: selectedInitiativesIntentValue,
      oneOnOne: selectedOneOnOneIntent?.value,
      review: selectedReviewsIntentValue,
      develop: selectedCompetenciesIntent?.value,
      engagement: selectedEngagementIntent?.value,
      disciplinaryActions: selectedDisciplinaryIntent?.value,
    }),
    [
      selectedGoalsIntentValue,
      selectedInitiativesIntentValue,
      selectedOneOnOneIntent?.value,
      selectedReviewsIntentValue,
      selectedCompetenciesIntent?.value,
      selectedEngagementIntent?.value,
      selectedDisciplinaryIntent?.value,
    ]
  );

  const applicableL2ModuleRedirection = useCallback(
    // eslint-disable-next-line no-shadow
    (l1Module: L1Module, teamType?: TeamType) => {
      switch (l1Module) {
        case 'myData':
          return applicableL2MyDataModules[0]?.value;
        case 'team':
          if (teamType === 'directReports') {
            return applicableL2DirectReportsModules[0]?.value;
          }
          return applicableL2AllReportsModules[0]?.value;

        case 'company':
          return applicableL2CompanyModules.length > 0
            ? applicableL2CompanyModules[0]?.value
            : staticRoutes.company.settings.platform.organization;
        default:
          return routes.home;
      }
    },
    [
      applicableL2AllReportsModules,
      applicableL2CompanyModules,
      applicableL2DirectReportsModules,
      applicableL2MyDataModules,
    ]
  );

  const defaultIntentRedirection = useCallback(
    // eslint-disable-next-line no-shadow
    (
      l1Module: L1Module,
      teamType?: TeamType,
      view?: {
        id: string;
        enabledFeatures: EnabledFeatures[];
      }
    ) => {
      if (l1Module === 'team') {
        if (teamType === 'view') {
          const applicableL2ViewModules = getApplicableL2ViewModules(moduleName, view?.enabledFeatures);
          const applicableL2Keys = applicableL2ViewModules.map((module) => module.key);
          if (
            applicableL2Keys.includes(l2Module as 'goal' | 'review') &&
            DEFAULT_INTENTS_FOR_MODULES[l2Module][l1Module][teamType]?.isAccessible()
          ) {
            const viewRoute = DEFAULT_INTENTS_FOR_MODULES[l2Module as 'goal' | 'review'][l1Module][teamType];
            return getViewRoute(viewRoute?.path, view?.id);
          }
          return getViewRoute(applicableL2ViewModules[0]?.value, view?.id);
        }
        if (DEFAULT_INTENTS_FOR_MODULES[l2Module]?.[l1Module]?.[teamType]?.isAccessible()) {
          return DEFAULT_INTENTS_FOR_MODULES[l2Module][l1Module][teamType]?.path;
        }
        return applicableL2ModuleRedirection(l1Module, teamType);
      }
      if (DEFAULT_INTENTS_FOR_MODULES[l2Module][l1Module]?.isAccessible()) {
        return DEFAULT_INTENTS_FOR_MODULES[l2Module][l1Module]?.path;
      }
      return applicableL2ModuleRedirection(l1Module, teamType);
    },
    [applicableL2ModuleRedirection, l2Module, moduleName]
  );

  const handleModuleWithIntentRedirection = useCallback(
    (
      l1Module: L1Module,
      teamType?: TeamType,
      view?: {
        id: string;
        enabledFeatures: EnabledFeatures[];
      }
    ) => {
      // if user is on homepage or overview pages then redirect to applicable module
      if (isHome || l2Module === 'overview') {
        if (l1Module === 'team' && teamType === 'view') {
          const applicableL2ViewModules = getApplicableL2ViewModules(moduleName, view?.enabledFeatures);
          return getViewRoute(applicableL2ViewModules[0]?.value, view?.id);
        }
        return applicableL2ModuleRedirection(l1Module, teamType);
      }
      // is user is on any L2 module other than overview
      // eslint-disable-next-line no-lonely-if
      if (l2Module && SELECTED_INTENT_VALUES[l2Module]) {
        // if is team
        if (l1Module === 'team' && SELECTED_INTENT_VALUES[l2Module][l1Module]) {
          if (teamType === 'view') {
            // handle view intent
            const applicableL2ViewModules = getApplicableL2ViewModules(moduleName, view?.enabledFeatures);
            // now finding whether the selected module (goals, reviews, etc) is enabled for the view or not
            const indexOfL2ModuleInView = applicableL2ViewModules.findIndex((module) => module.key === l2Module);
            if (indexOfL2ModuleInView !== -1 && SELECTED_INTENT_VALUES[l2Module][l1Module].view?.isAccessible()) {
              return getViewRoute(SELECTED_INTENT_VALUES[l2Module][l1Module].view?.path, view?.id);
            }
            // check for default route if not then applicable route
            return defaultIntentRedirection(l1Module, teamType, {
              enabledFeatures: view?.enabledFeatures,
              id: view?.id,
            });
          }
          if (SELECTED_INTENT_VALUES[l2Module][l1Module][teamType]?.isAccessible()) {
            return SELECTED_INTENT_VALUES[l2Module][l1Module][teamType]?.path;
          }
          // check for default route if not then applicable route
          return defaultIntentRedirection(l1Module, teamType);
        }
        if (SELECTED_INTENT_VALUES[l2Module][l1Module] && l1Module !== 'team') {
          if (SELECTED_INTENT_VALUES[l2Module][l1Module]?.isAccessible()) {
            return SELECTED_INTENT_VALUES[l2Module][l1Module]?.path;
          }
          // check for default route if not then applicable route
          return defaultIntentRedirection(l1Module, teamType);
        }
        // check for default route if not then applicable route
        return defaultIntentRedirection(l1Module, teamType, {
          id: view?.id,
          enabledFeatures: view?.enabledFeatures,
        });
      }
      return applicableL2ModuleRedirection(l1Module, teamType);
    },
    [isHome, l2Module, SELECTED_INTENT_VALUES, applicableL2ModuleRedirection, defaultIntentRedirection, moduleName]
  );

  return {
    handleModuleWithIntentRedirection,
  };
};
